import { useBlockContext } from '@smartfire/js-block-engine'
import { useCallback, useEffect, useState } from 'react'
import { filterObjectByKey, isObjectShallowEqual, xorDecipherAndParse } from '../../shared/utils'
import { Params, PARAMS_ATTRIBUTES } from './useParamsHandler'
import { apiFetchWithTimeout } from '../../shared/apiFetchWithTimeout'
import { CONSTS } from '../../config/const'
import { PageType, getCompetitionPageType } from '../../competitions/types'

interface UseAttributesQueryReturn<T> {
    attributes: T
    isLoading: boolean
}

const PAGE_TYPE_FETCH_STRATEGIES: {
    [x in PageType]: 'fetch' | 'reload'
} = {
    tunnel: 'fetch',
    competition: 'fetch',
    classement: 'reload',
    statistiques: 'reload',
    recherche: 'reload',
    actualites: 'reload',
    rencontre: 'reload',
}

export const keepOnlyAttributes = <T>(attributesWithParams: T & Params): T => {
    return filterObjectByKey(attributesWithParams, (key) => !PARAMS_ATTRIBUTES.includes(key)) as T
}

export const useAttributesQuery = <T>({
    initAttributes,
    initParams,
    currentParams,
    onFinish,
}: {
    initAttributes: T
    initParams: Params
    currentParams: Params
    onFinish?: ({
        message,
    }: {
        message: 'UPDATED_ATTRIBUTES' | 'PARAMS_ARE_SHALLOWLY_EQUAL'
    }) => void
}): UseAttributesQueryReturn<T> => {
    const pageType = getCompetitionPageType()
    const blockContext = useBlockContext()

    const blockLoadedName = blockContext.Block['loadedName']

    const [attributes, setAttributes] = useState<T>(initAttributes)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const updateAttributes = useCallback(async () => {
        setIsLoading(true)
        const queryParams = new URLSearchParams({
            block: blockLoadedName,
            ...currentParams,
        })
        try {
            if (PAGE_TYPE_FETCH_STRATEGIES[pageType] === 'reload') {
                window.location.reload() // Url is already changed by useParamsHandler
                return
            }
            const fetchedAttributes = await apiFetchWithTimeout<string>(
                {
                    path: `competitions/v1/computeBlockAttributes?${queryParams.toString()}`,
                },
                CONSTS.REFRESH_BLOCK_ATTRIBUTES_API_REQUEST_TIMEOUT_IN_MS,
            )

            const jsonAttributes = xorDecipherAndParse(fetchedAttributes)

            setAttributes(keepOnlyAttributes(jsonAttributes))
            onFinish?.({ message: 'UPDATED_ATTRIBUTES' })
        } catch (e: any) {
            if (e.name === 'AbortError') {
                console.error('Request has been aborted after timeout')
            }
            console.error('Error during js refetching, force reloading window')
            window.location.reload() // Url is already changed by useParamsHandler
        }
        setIsLoading(false)
    }, [blockLoadedName, currentParams, onFinish, pageType])

    useEffect(() => {
        if (isObjectShallowEqual(initParams, currentParams)) {
            setAttributes(initAttributes)
            onFinish?.({ message: 'PARAMS_ARE_SHALLOWLY_EQUAL' })
            return
        }

        updateAttributes()
        // As initXXX are objects, we want to avoid infinite loop (Objects can't change)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentParams, updateAttributes, onFinish])

    return { attributes, isLoading }
}
